import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { useToast } from "@/components/feedback";
import { useAccessToken, useTenantData } from "@/hooks";
import { QueryKeys } from "@/lib/types";
import { createAuthenticatedApi } from "@/lib/utils";
import { getApiErrorMessage } from "@/lib/utils/api/error-message";
import type { RefundPaymentRequest } from "@/openapi/generated";
import { PaymentApi } from "@/openapi/generated";

export const usePaymentRefund = (toast: ReturnType<typeof useToast>) => {
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();
  const { tenantId } = useTenantData();

  return useMutation({
    mutationFn: async (refundPaymentRequest: RefundPaymentRequest) => {
      if (!accessToken) {
        throw new Error("Authentication token is required");
      }
      const service = createAuthenticatedApi(PaymentApi, accessToken);
      await service.apiPaymentsRefundPut({
        refundPaymentRequest,
        xTenantID: tenantId,
      });
      return refundPaymentRequest;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        predicate: (q) =>
          Array.isArray(q.queryKey) && q.queryKey[0] === QueryKeys.Payments,
      });
    },
    onError: async (error) => {
      const message = await getApiErrorMessage(
        error,
        "Failed to refund payment",
      );
      toast({
        title: "Refund failed",
        description: message,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    },
  });
};
